/**
 * v3.15-1151 — Clear To Collect modal.
 *
 * Two-state modal:
 *   1. Confirmation panel — shows the snapshot (offer #, deal amount,
 *      PTC fee, authorizer, current time). The rep clicks "Authorize" to
 *      issue the auth.
 *   2. Issued panel — green status badge, full record, auto-closes after
 *      ~3s or on close click. The same modal is used to VIEW an existing
 *      active authorization (read-only mode when `existing` prop is set).
 *
 * Props:
 *   open / onOpenChange  — Dialog controls
 *   client               — the client record (full_name, id)
 *   selectedOffer        — the currently-selected financing_offers row
 *                          (id, offer_number, deal_amount, lender_name)
 *   existing             — existing active authorization to display
 *                          (read-only mode). null = issue mode.
 *   isStale              — bool, when `existing` is set
 *   staleReason          — string, when `existing` is set
 *   onSuccess()          — called after a successful authorize
 *
 * Acceptance rules enforced client-side BEFORE submit:
 *   - selectedOffer must exist
 *   - selectedOffer.deal_amount must coerce to a positive number
 *   - PTC Fee always rendered = Deal Amount × 10%
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Loader2, ShieldCheck, AlertTriangle, X, FileText, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const fmtUSD = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const v = Number(String(n).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const computePtcFee = (dealAmount) => {
  if (dealAmount === null || dealAmount === undefined || dealAmount === '') return null;
  const v = Number(String(dealAmount).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(v) || v <= 0) return null;
  return Math.round(v * 0.1 * 100) / 100;
};

const ClearToCollectModal = ({ open, onOpenChange, client, selectedOffer, existing, isStale, staleReason, currentUser, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState(null);   // populated after a successful issue

  // Snapshot — read-only or live preview.
  const view = useMemo(() => {
    // Prefer the issued doc (just authorized) → existing → live preview.
    const src = issued || existing;
    if (src && !isStale) {
      return {
        mode: 'view',
        dealAmount:  src.deal_amount,
        ptcFee:      src.ptc_fee,
        offerNumber: src.offer_number,
        offerId:     src.offer_id,
        lenderName:  src.lender_name,
        authorizedBy:   src.authorized_by_user_name,
        authorizedAt:   src.authorized_at,
        statusLabel:    'Approved for Collection',
        statusTone:     'emerald',
      };
    }
    if (src && isStale) {
      const live = selectedOffer || {};
      return {
        mode: 'stale',
        dealAmount:  src.deal_amount,
        ptcFee:      src.ptc_fee,
        offerNumber: src.offer_number,
        offerId:     src.offer_id,
        lenderName:  src.lender_name,
        authorizedBy:   src.authorized_by_user_name,
        authorizedAt:   src.authorized_at,
        statusLabel:    'Collection Authorization Outdated',
        statusTone:     'red',
        staleReason,
        // Live values for the reissue panel
        liveDealAmount:  live.deal_amount,
        liveOfferNumber: live.offer_number,
        livePtcFee:      computePtcFee(live.deal_amount),
      };
    }
    const live = selectedOffer || {};
    return {
      mode: 'issue',
      dealAmount:  live.deal_amount,
      ptcFee:      computePtcFee(live.deal_amount),
      offerNumber: live.offer_number,
      offerId:     live.id,
      lenderName:  live.lender_name,
      authorizedBy:   currentUser?.full_name || currentUser?.email,
      authorizedAt:   new Date().toISOString(),
      statusLabel:    'Pending Authorization',
      statusTone:     'amber',
    };
  }, [issued, existing, isStale, staleReason, selectedOffer, currentUser]);

  const canIssue = useMemo(() => {
    const v = computePtcFee(selectedOffer?.deal_amount);
    return !!selectedOffer && v !== null && !submitting;
  }, [selectedOffer, submitting]);

  const issue = async () => {
    if (!canIssue) {
      toast.error('No selected financing offer with a Deal Amount.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/clear-to-collect`);
      setIssued(data?.authorization || null);
      toast.success('Clear To Collect issued.');
      onSuccess?.(data?.authorization);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue Clear To Collect');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const TONE = {
    emerald: { bar: 'bg-emerald-700', dot: 'bg-emerald-500', text: 'text-emerald-700', pill: 'bg-emerald-100 text-emerald-800' },
    amber:   { bar: 'bg-amber-600',   dot: 'bg-amber-500',   text: 'text-amber-700',   pill: 'bg-amber-100 text-amber-800' },
    red:     { bar: 'bg-red-700',     dot: 'bg-red-500',     text: 'text-red-700',     pill: 'bg-red-100 text-red-800' },
  };
  const tone = TONE[view.statusTone] || TONE.amber;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4"
      style={{ zIndex: 100001 }}
      role="dialog"
      aria-modal="true"
      data-testid="clear-to-collect-modal"
    >
      <div className="w-full max-w-md max-h-[92vh] overflow-hidden rounded-xl shadow-2xl bg-white flex flex-col">
        {/* Header */}
        <div className={`px-5 py-4 ${tone.bar} text-white flex items-start gap-3`}>
          <Receipt className="h-6 w-6 shrink-0" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider opacity-80">
              Clear To Collect · v3.15-1151
            </div>
            <div className="font-semibold text-base leading-tight">
              {client?.full_name || 'Client'}
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="text-white/80 hover:text-white p-1 -m-1"
            data-testid="ctc-close-btn"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Status row */}
          <div className="flex items-center gap-2" data-testid="ctc-status-row">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone.pill}`}>
              <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
              {view.statusLabel}
            </span>
          </div>

          {view.mode === 'stale' && (
            <div className="border border-red-300 bg-red-50 text-red-900 rounded p-3 text-xs flex gap-2"
                 data-testid="ctc-stale-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">A new Clear To Collect is required.</div>
                <div className="opacity-90">
                  {view.staleReason || 'Financing changed after the previous authorization.'}
                </div>
              </div>
            </div>
          )}

          {/* Stale: side-by-side OLD vs NEW */}
          {view.mode === 'stale' ? (
            <>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Previous authorization</div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs space-y-1 opacity-70 line-through">
                <div className="flex justify-between"><span>Authorized amount</span><span className="font-semibold">{fmtUSD(view.dealAmount)}</span></div>
                <div className="flex justify-between"><span>PTC Fee (10%)</span><span>{fmtUSD(view.ptcFee)}</span></div>
                <div className="flex justify-between"><span>Offer</span><span>{view.offerNumber || '—'}</span></div>
                <div className="flex justify-between"><span>By</span><span>{view.authorizedBy || '—'}</span></div>
                <div className="flex justify-between"><span>At</span><span>{fmtDateTime(view.authorizedAt)}</span></div>
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Current financing</div>
              <SnapshotPanel
                dealAmount={view.liveDealAmount}
                ptcFee={view.livePtcFee}
                offerNumber={view.liveOfferNumber}
                lenderName={selectedOffer?.lender_name}
                authorizedBy={currentUser?.full_name || currentUser?.email}
                authorizedAt={new Date().toISOString()}
                editable
              />
            </>
          ) : (
            <SnapshotPanel
              dealAmount={view.dealAmount}
              ptcFee={view.ptcFee}
              offerNumber={view.offerNumber}
              lenderName={view.lenderName}
              authorizedBy={view.authorizedBy}
              authorizedAt={view.authorizedAt}
              editable={view.mode === 'issue'}
            />
          )}

          {view.mode === 'issue' && (
            <div className="text-[10px] text-slate-500 leading-relaxed">
              Pressing Authorize permanently records this snapshot, notifies
              the processing team, and writes the authorization to the
              client&apos;s activity timeline. The customer may be charged
              <span className="font-semibold"> {fmtUSD(view.dealAmount)}</span>;
              the PTC Fee is <span className="font-semibold">{fmtUSD(view.ptcFee)}</span>.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="ctc-cancel-btn"
          >
            {view.mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {(view.mode === 'issue' || view.mode === 'stale') && (
            <Button
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={issue}
              disabled={!canIssue}
              data-testid="ctc-authorize-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              {view.mode === 'stale' ? 'Reissue Authorization' : 'Authorize Collection'}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const SnapshotPanel = ({ dealAmount, ptcFee, offerNumber, lenderName, authorizedBy, authorizedAt, editable }) => (
  <div
    className={`rounded border ${editable ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'} p-3 text-xs space-y-1.5`}
    data-testid="ctc-snapshot-panel"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="text-slate-500 uppercase tracking-wider text-[10px]">Authorized Amount to Charge Customer</span>
      <span className="font-bold text-sm text-slate-800 text-right" data-testid="ctc-deal-amount">
        {fmtUSD(dealAmount)}
      </span>
    </div>
    <div className="flex items-start justify-between gap-2">
      <span className="text-slate-500 uppercase tracking-wider text-[10px]">PTC Fee (10%)</span>
      <span className="font-semibold text-slate-700 text-right" data-testid="ctc-ptc-fee">
        {fmtUSD(ptcFee)}
      </span>
    </div>
    <div className="border-t border-slate-200 pt-1.5 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <span className="text-slate-500 text-[10px] uppercase tracking-wider">Selected Financing Offer</span>
        <span className="text-slate-700 text-right" data-testid="ctc-offer-number">
          {offerNumber ? <>Offer #{offerNumber}</> : '—'}
          {lenderName ? <span className="text-slate-400"> · {lenderName}</span> : null}
        </span>
      </div>
      <div className="flex items-start justify-between gap-2">
        <span className="text-slate-500 text-[10px] uppercase tracking-wider">Authorized By</span>
        <span className="text-slate-700 text-right" data-testid="ctc-authorized-by">{authorizedBy || '—'}</span>
      </div>
      <div className="flex items-start justify-between gap-2">
        <span className="text-slate-500 text-[10px] uppercase tracking-wider">Date / Time</span>
        <span className="text-slate-700 text-right" data-testid="ctc-authorized-at">{fmtDateTime(authorizedAt)}</span>
      </div>
    </div>
  </div>
);

export default ClearToCollectModal;
